// packages/storefront/src/translation/reportWidgetError.ts
import type { ClientErrorReport, ClientErrorReportDetails } from '@tourtask/types/api/v1/ClientErrorReport';
import { CLIENT_ERROR_REPORT_LIMITS as L } from '@tourtask/constants/inputs/clientErrorReport';
import { publicApiRequest } from '@tourtask/storefront/api/publicApiRequest';
import { storefrontEnv } from '@tourtask/storefront/storefrontEnv';
import { DOM_GUARD_REGISTRY_KEY, type DomGuardRegistryV1 } from './domGuard';
import type { DomGuardEventKind } from './domGuardImpl';

/** The embed's widgets — one bundle each (`entries/shop.tsx`, `chat.tsx`, `reviews.tsx`, `terms.tsx`). */
export type WidgetName = 'storefront' | 'chat' | 'reviews' | 'terms';

/** React's three root callbacks, or a DOM guard event (docs/embed-browser-translation.md). */
export type WidgetErrorKind = 'caught' | 'uncaught' | 'recoverable' | `guard:${DomGuardEventKind}`;

/** From the root's `data-business-id` / `data-business-token`. */
export interface WidgetTelemetry {
	bizId: string;
	bizToken: string;
}

/** Network reports per bundle per page view (module state lives exactly that long). */
const MAX_REPORTS_PER_PAGE_VIEW = 5;
const TIMEOUT_MS = 5000;

const telemetryByWidget = new Map<WidgetName, WidgetTelemetry | null>();
const sentKeys = new Set<string>();
const seenErrors = new WeakSet<object>();
let sentCount = 0;

/** Called once per root by `mountWidgetRoot`, before anything can report. `null` = console only. */
export function configureWidgetErrorReporting(widget: WidgetName, telemetry: WidgetTelemetry | null): void {
	telemetryByWidget.set(widget, telemetry);
}

/**
 * Error and DOM-guard telemetry for the embed widgets, which run on partner
 * pages we cannot see. Every report goes to the partner page's console, and —
 * when the widget was configured with its business credentials — to
 * `POST /v1/businesses/{bizId}/client-errors`, which logs it in pm2 as a
 * `[client-error]` line. Without the second half, how the translation guard
 * behaves in Safari, Firefox and Edge on real partner pages stays invisible.
 *
 * Rules, all load-bearing:
 * - **Never throws, never shows anything.** Everything is inside `try`; a
 *   telemetry failure is swallowed and never retried. This runs from React's
 *   error callbacks and the guard's event listener, where a throw would turn a
 *   report into a second failure.
 * - **Bounded.** At most {@link MAX_REPORTS_PER_PAGE_VIEW} network reports per
 *   bundle per page view, each `(kind, error.name)` at most once. The console
 *   line is not capped.
 * - **No text content, no PII.** The URL is origin + pathname (no query string,
 *   no hash); nothing the widget rendered is read. The guard's counters and
 *   page-translation hints are booleans, numbers and DOM member names.
 * - **One report per error object.** A `WidgetErrorBoundary` catch reaches both
 *   the root's `onCaughtError` and the boundary's own `componentDidCatch`; the
 *   second is dropped by identity.
 */
export function reportWidgetError(r: {
	widget: WidgetName;
	kind: WidgetErrorKind;
	error?: unknown;
	componentStack?: string;
}): void {
	try {
		if (typeof r.error === 'object' && r.error !== null) {
			if (seenErrors.has(r.error)) return;
			seenErrors.add(r.error);
		}

		const guard = readGuardStats();
		const isGuardEvent = r.kind.startsWith('guard:');
		// Set by the bundle that mounted the widget; `dev` for a local build.
		const build = storefrontEnv().buildId;
		// The partner's page owns the console, and it is the only trace a support
		// session has when telemetry is off (no credentials) or has hit its cap.
		console.error(
			`[TourTask ${r.widget} ${build}] ${r.kind}`,
			...(r.error !== undefined ? [r.error] : []),
			...(r.componentStack ? [r.componentStack] : []),
			...(isGuardEvent && guard ? [guard] : []),
		);

		const telemetry = telemetryByWidget.get(r.widget);
		if (!telemetry || sentCount >= MAX_REPORTS_PER_PAGE_VIEW) return;

		const described = r.error === undefined ? null : describeError(r.error);
		const dedupeKey = `${r.kind}|${described?.name ?? ''}`;
		if (sentKeys.has(dedupeKey)) return;
		sentKeys.add(dedupeKey);
		sentCount++;

		const report: ClientErrorReport = {
			kind: r.kind.slice(0, L.kind),
			// A guard event carries no error: its kind is the whole message.
			message: described ? described.message.slice(0, L.message) : '',
			...(described?.stack ? { stack: described.stack.slice(0, L.stack) } : {}),
			...(r.componentStack ? { componentStack: r.componentStack.slice(0, L.componentStack) } : {}),
			url: `${window.location.origin}${window.location.pathname}`.slice(0, L.url),
			userAgent: window.navigator.userAgent.slice(0, L.userAgent),
			deployId: build.slice(0, L.deployId),
			ts: Date.now(),
			details: readPageDetails(r.widget, guard),
		};

		void publicApiRequest<null>({
			path: `businesses/${encodeURIComponent(telemetry.bizId)}/client-errors`,
			method: 'POST',
			// Spread into a plain record: `publicApiRequest` takes an index signature.
			body: { ...report },
			bizToken: telemetry.bizToken,
			timeoutMs: TIMEOUT_MS,
		}).catch(() => undefined);
	} catch {
		// Telemetry must never become a failure of its own.
	}
}

/** Name, one-line message and stack of whatever was thrown — `throw` accepts any value. */
function describeError(error: unknown): { name: string; message: string; stack?: string } {
	if (typeof error === 'object' && error !== null && 'message' in error && typeof error.message === 'string') {
		// Error, DOMException (NotFoundError is one) and cross-realm errors alike.
		const name = 'name' in error && typeof error.name === 'string' ? error.name : 'Error';
		const stack = 'stack' in error && typeof error.stack === 'string' ? error.stack : undefined;
		return { name, message: `${name}: ${error.message}`, stack };
	}
	// A thrown non-Error: say what it was, never its content (it could be anything).
	const type = error === null ? 'null' : typeof error;
	return { name: `non-error:${type}`, message: `A non-Error value was thrown (${type})` };
}

/**
 * The page-wide guard's counters, flattened to scalars: numbers and booleans
 * as they are, and the `diag` member-name lists (`foreignBelow`, `notOnTop`:
 * which of `removeChild` / `insertBefore` / `appendChild` / `nodeValue` another
 * script patched) comma-joined. Read through the frozen registry key, so it
 * reports whichever impl governs the page — possibly another bundle's.
 * `undefined` when no protocol-1 guard is installed, or when reading it fails
 * (the report still goes out, without the counters).
 */
function readGuardStats(): Record<string, number | boolean | string> | undefined {
	try {
		const candidate: unknown = Reflect.get(window, DOM_GUARD_REGISTRY_KEY);
		if (typeof candidate !== 'object' || candidate === null || !('protocol' in candidate) || candidate.protocol !== 1) {
			return undefined;
		}
		// Protocol 1 freezes the registry's shape (fields are only ever added).
		const reg = candidate as DomGuardRegistryV1;
		if (!reg.impl) return undefined;

		// The two diag lists first: a later impl may add counters, and the cap
		// must never cut these.
		const out: Record<string, number | boolean | string> = {
			foreignBelow: reg.diag.foreignBelow.join(',').slice(0, L.guardString),
			notOnTop: reg.diag.notOnTop.join(',').slice(0, L.guardString),
		};
		for (const [key, value] of Object.entries(reg.impl.stats())) {
			if (typeof value === 'number' || typeof value === 'boolean') out[key.slice(0, L.guardKey)] = value;
		}
		return Object.fromEntries(Object.entries(out).slice(0, L.guardEntries));
	} catch {
		return undefined;
	}
}

/** Which widget, the guard's counters, and whether some engine has translated the page. */
function readPageDetails(
	widget: WidgetName,
	guard: Record<string, number | boolean | string> | undefined,
): ClientErrorReportDetails {
	const html = document.documentElement;
	return {
		widget,
		...(guard ? { guard } : {}),
		htmlLang: html.lang.slice(0, L.htmlLang),
		// Chrome's built-in Google Translate and the Google website widget.
		translatedClass: /translated-(ltr|rtl)/.test(html.className),
		// Edge (Microsoft Translator) stamps every element it translated.
		msTranslated: document.querySelector('[_msttexthash]') !== null,
		// Firefox full-page translation.
		mozTranslated: document.querySelector('[data-moz-translations-id]') !== null,
	};
}
